import { useState, useMemo, useCallback } from 'react'
import toast from 'react-hot-toast'
import Paper from '@/components/Paper'
import {
    DataTableCustomized,
    FilterBar,
    DataTableAction,
    DataTableSelectionAction,
} from '@/components/ui/data-table-customized'
import { useConfirmation } from '@/contexts/confirmation'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import useQueryClientCallback from '@/hooks/useQueryClientCallback'
import CancelIcons from '@/icons/CancelIcons'
import ConfirmServiceIcons from '@/icons/ConfirmServiceIcons'
import { confirmService, rejectedService } from '@/modules/Service/services'
import { Service } from '@/modules/Service/types'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useWaitingConfirmList } from '@/modules/Service/hooks'

const WaitingConfirm = () => {
    const { refetch } = useQueryClientCallback({
        reactQueryKeys: ['services.all', 'services.waitings.confirms'],
    })

    const [selectedKeys, setSelectedKeys] = useState<string[]>([])
    const { setConfirmation } = useConfirmation()

    const {
        services,
        pagination,
        stateComponent,
        isFetching,
        params,
        control,
        setFilter,
        searchInputProps,
        columns,
        filterItems,
        handleSortChange,
        handlePageChange,
        handlePageSizeChange,
        t,
    } = useWaitingConfirmList()

    // Mutations
    const { mutateAsync } = useMutationWithHandler(
        { mutationFn: confirmService },
        {
            successMessage: t('confirm-service-success'),
            reactQueryKeys: QUERY_KEYS.SERVICES,
            onSuccess: () => refetch(),
        }
    )

    const { mutateAsync: rejectService } = useMutationWithHandler(
        { mutationFn: rejectedService },
        {
            successMessage: t('rejected-service-success'),
            reactQueryKeys: QUERY_KEYS.SERVICES,
            onSuccess: () => refetch(),
        }
    )

    // Row actions
    const rowActions = useMemo<DataTableAction<Service>[]>(
        () => [
            {
                key: 'confirm',
                label: t('confirm-service'),
                icon: <ConfirmServiceIcons className="w-4 h-4" />,
                variant: 'success',
                hidden: (row) => row?.service_status !== 'waiting-confirm',
                onClick: (row) => {
                    if (
                        !row?.employee_sale_tracking_ids ||
                        row?.employee_sale_tracking_ids.length === 0
                    ) {
                        toast.error(t('employee-sale-tracking-required'))
                        return
                    }
                    setConfirmation({
                        open: true,
                        title: t('confirm-service'),
                        description: t('confirm-service-warning'),
                        onConfirm: () => mutateAsync(row?.service_id),
                    })
                },
            },
            {
                key: 'reject',
                label: t('service-rejected'),
                icon: <CancelIcons className="w-4 h-4" />,
                variant: 'danger',
                hidden: (row) => row?.service_status !== 'waiting-confirm',
                onClick: (row) => {
                    setConfirmation({
                        open: true,
                        useReason: true,
                        title: t('rejected-service'),
                        description: t('rejected-service-warning'),
                        onConfirm: (reason) => {
                            rejectService({
                                serviceID: row?.service_id,
                                reason: reason as string,
                            })
                        },
                    })
                },
            },
        ],
        [t, mutateAsync, rejectService, setConfirmation]
    )

    // Bulk approve handler
    const handleBulkApprove = useCallback(
        async (selectedRows: Service[]) => {
            // Check if any selected service doesn't have employee_sale_tracking_ids
            const servicesWithoutSaleTracking = selectedRows.filter(
                (service) =>
                    !service?.employee_sale_tracking_ids ||
                    service?.employee_sale_tracking_ids.length === 0
            )

            if (servicesWithoutSaleTracking.length > 0) {
                const serviceNames = servicesWithoutSaleTracking
                    .map((s) => `${s.product_group_name} - ${s.service_name}`)
                    .join(', ')
                toast.error(
                    t('employee-sale-tracking-required-for-services', {
                        services: serviceNames,
                    })
                )
                return
            }

            await Promise.allSettled(
                selectedRows.map((s) => mutateAsync(s.service_id))
            )
            setSelectedKeys([])
        },
        [mutateAsync, t]
    )

    // Selection actions
    const selectionActions = useMemo<DataTableSelectionAction<Service>[]>(
        () => [
            {
                key: 'bulk-confirm',
                label: t('confirm-service'),
                icon: <ConfirmServiceIcons className="w-4 h-4" />,
                variant: 'success',
                onClick: (selectedRows) => {
                    setConfirmation({
                        open: true,
                        title: t('confirm-service'),
                        description: t('confirm-service-warning', {
                            'service-ids': selectedRows
                                .map((s) => s.service_id)
                                .join(', '),
                        }),
                        onConfirm: () => handleBulkApprove(selectedRows),
                    })
                },
            },
        ],
        [t, setConfirmation, handleBulkApprove]
    )

    return (
        <Paper>
            <FilterBar
                control={control}
                setFilter={setFilter}
                items={filterItems}
                hideSearch
            />

            {stateComponent || (
                <DataTableCustomized
                    columns={columns}
                    rows={services}
                    rowKey={(row) => String(row?.service_id)}
                    size="small"
                    resizable
                    loading={!services.length && !pagination}
                    isFetching={isFetching}
                    emptyMessage={t('no-services')}
                    actions={rowActions}
                    selection={{
                        selectedKeys,
                        onSelectionChange: setSelectedKeys,
                        getRowKey: (row) => String(row?.service_id),
                        actions: selectionActions,
                    }}
                    sorting={{
                        sortBy: params.sort_by,
                        sortOrder: params.sort_order,
                        onSortChange: handleSortChange,
                    }}
                    pagination={{
                        page: params.page || 1,
                        pageSize: params.page_size || 20,
                        total: pagination?.total || 0,
                        onPageChange: handlePageChange,
                        onPageSizeChange: handlePageSizeChange,
                        pageSizeOptions: [10, 20, 50, 100],
                    }}
                    toolbar={{
                        search: {
                            ...searchInputProps,
                            placeholder: t('search-service'),
                        },
                        settings: {
                            show: true,
                            storageKey: 'services-waiting-confirm-list',
                        },
                    }}
                />
            )}
        </Paper>
    )
}

export default WaitingConfirm
